/**
 * bridge/biometrics_lock_controller.js — "biometrics-lock" bridge component
 * (Stimulus + Strada).
 *
 * Capability-tier bridge component: re-authenticates the user with Face ID /
 * fingerprint (or device passcode fallback) before the locked app is revealed.
 * Chippie locks on background and asks for an explicit unlock when the user
 * returns; any web flow that needs a fresh re-auth can also call it directly.
 *
 * Stimulus identifier "bridge--biometrics-lock" (glob-derived from this subdir);
 * Strada component name "biometrics-lock" MUST match the iOS class var name
 * (native/ios/Chippie/Bridge/BiometricsLockComponent.swift) and the Android
 * @JavascriptInterface method name (ChippieNativeBridge.biometricAuthenticate).
 *
 * Why it extends Strada's BridgeComponent: the iOS prompt is reached via
 * this.send("authenticate", ...), which only works on a connected bridge
 * component inside the iOS Hotwire Native shell. Extending BridgeComponent gives
 * us that send() transport AND the UA-gated connect() (only connects in a native
 * shell, so registering the live iOS send is safe).
 *
 * Three-way transport via the window.ChippieBridge facade ({ ios, android, pwa }).
 * The facade method is ASYNC — callers await the boolean unlock result:
 *
 *   const ok = await window.ChippieBridge.biometricsLock({ reason: "Unlock Chippie" });
 *   if (ok) dropLockOverlay(); else showRetry();
 *
 *   - ios:     this.send("authenticate", { reason }, reply) -> resolves with
 *              reply.data.success (LocalAuthentication).
 *   - android: window.ChippieNative.biometricAuthenticate(reason, token); the
 *              native side runs BiometricPrompt and calls back into the page via
 *              window.ChippieBridge._biometricsLockResolve(token, success). We
 *              bridge that one-shot native->web callback to the awaited Promise
 *              through a token registry (Android's @JavascriptInterface is
 *              fire-and-forget, so it cannot return the result inline).
 *   - pwa:     no Web API can drive the OS biometric lock screen. There is no
 *              honest browser equivalent, so the PWA fallback resolves true
 *              (unlock is a no-op) rather than faking a credential check. The lock
 *              UX is a native-shell capability; on the web the overlay simply
 *              isn't shown. This is the documented graceful no-op for Android-web
 *              and desktop alike.
 *
 * Template wiring is optional (most callers fire it programmatically), e.g.:
 *   <div data-controller="bridge--biometrics-lock"></div>
 * then from JS: await window.ChippieBridge.biometricsLock();
 *
 * Web side follows the sibling share_controller / haptic_controller facade pattern.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Token registry bridging Android's fire-and-forget @JavascriptInterface back to
// the awaited Promise. Each call stashes its resolver under a unique token; the
// native side invokes window.ChippieBridge._biometricsLockResolve(token, success)
// which looks the resolver up and settles it. Module-scoped so it survives the
// controller connect/disconnect churn that Turbo navigation causes.
const pendingAndroidUnlocks = new Map();
let unlockToken = 0;

function installAndroidResolver() {
  if (!window.ChippieBridge) return;
  // Idempotent: a single resolver shared by all calls, keyed by token.
  window.ChippieBridge._biometricsLockResolve = (token, success) => {
    const resolve = pendingAndroidUnlocks.get(token);
    if (!resolve) return;
    pendingAndroidUnlocks.delete(token);
    resolve(Boolean(success));
  };
}

function androidAuthenticate(reason) {
  return new Promise((resolve) => {
    if (!(window.ChippieNative && window.ChippieNative.biometricAuthenticate)) {
      // Bridge method missing (older shell) — fail closed: stay locked.
      resolve(false);
      return;
    }
    const token = String(++unlockToken);
    pendingAndroidUnlocks.set(token, resolve);
    try {
      window.ChippieNative.biometricAuthenticate(reason, token);
    } catch (_e) {
      pendingAndroidUnlocks.delete(token);
      resolve(false);
    }
  });
}

// Android + PWA impls are platform-static (no per-instance state), so register
// them once at module load. The facade is the SSOT for which one runs per
// platform; the controller's connect() upgrades the ios slot to a live send.
function registerStandalone() {
  if (!window.ChippieBridge || !window.ChippieBridge.register) return;
  installAndroidResolver();
  window.ChippieBridge.register("biometricsLock", {
    // ios is upgraded by connect() with a real send; this placeholder fails
    // closed (stays locked) if selected before the iOS shell connects.
    ios: () => Promise.resolve(false),
    android: ({ reason } = {}) => androidAuthenticate(reason || "Unlock Chippie"),
    // No OS biometric lock screen exists on the web; unlock is a no-op.
    pwa: () => Promise.resolve(true),
  });
}

// The facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
  registerStandalone();
} else {
  document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
  static component = "biometrics-lock";

  connect() {
    super.connect();
    // Inside the iOS shell now: wire the real Strada send into the facade,
    // keeping the Android/PWA fallbacks intact.
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    installAndroidResolver();
    window.ChippieBridge.register("biometricsLock", {
      ios: ({ reason } = {}) =>
        new Promise((resolve) => {
          this.send("authenticate", { reason: reason || "Unlock Chippie" }, (reply) => {
            resolve(Boolean(reply && reply.data && reply.data.success));
          });
        }),
      android: ({ reason } = {}) => androidAuthenticate(reason || "Unlock Chippie"),
      pwa: () => Promise.resolve(true),
    });
  }

  disconnect() {
    // Leaving the iOS shell: drop the live-send impl back to the standalone
    // placeholder so a stale `this.send` is never invoked.
    registerStandalone();
    super.disconnect();
  }
}
